import { FC, useMemo } from 'react';

import { ColumnTable, Entry } from '@/components/ColumnTable';
import { useSemanticModel } from '@/hooks/dbtStrategy/useSemanticModel';
import { useCommonLineageEntries } from '@/hooks/useCommonLineageEntries';
import { PanelDetails, ResourceDetailsParams } from './panelDetailTypes';
import { TagLinks } from './TagLinks';

export const useSemanticModelDetailsPanel = ({
  uniqueId,
  projectName,
}: ResourceDetailsParams): PanelDetails => {
  const semanticModelQuery = useSemanticModel(uniqueId);

  const commonEntries = useCommonLineageEntries(projectName);

  return useMemo(() => {
    const semanticModel = semanticModelQuery.data.definition;

    if (!semanticModel) {
      return { ...semanticModelQuery, rows: null, description: null };
    }

    const rows: Entry[] = [
      ...commonEntries,
      {
        key: 'Tags',
        data: <TagLinks tags={semanticModel.tags} />,
      },
    ];

    return { ...semanticModelQuery, rows, description: semanticModel.description };
  }, [semanticModelQuery, commonEntries]);
};

export const SemanticModelDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  ...props
}) => {
  const semanticModelDetails = useSemanticModelDetailsPanel(props);

  return (
    <div className={className}>
      {children?.(semanticModelDetails)}
      <ColumnTable
        tableEntries={semanticModelDetails.rows}
        compact={compact}
        isLoading={semanticModelDetails.fetching}
      />
    </div>
  );
};
